'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";

export type AutosaveState = 'running' | 'success' | 'error' | null;

export const useAutosave = <T>({
    enabled = true,
    guard = () => true,
    saveHandler,
}: {
    enabled: boolean;
    guard?: () => boolean;
    saveHandler: () => Promise<void>;
}, trigger: T[]): {
    autosaveState: AutosaveState | null;
    error?: string;
} => {
    const [autosaveState, setAutosaveState] = useState<AutosaveState | null>(null);
    const [error, setError] = useState<string | undefined>(undefined);

    const handlerRef = useRef(saveHandler);
    const guardRef = useRef(guard);
    const enabledRef = useRef(enabled);
    const isSavingRef = useRef(false);
    const hasPendingSaveRef = useRef(false);
    const resetStateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => { handlerRef.current = saveHandler; }, [saveHandler]);
    useEffect(() => { guardRef.current = guard; }, [guard]);
    useEffect(() => { enabledRef.current = enabled; }, [enabled]);

    const clearResetStateTimeout = useCallback(() => {
        if (resetStateTimeoutRef.current) {
            clearTimeout(resetStateTimeoutRef.current);
            resetStateTimeoutRef.current = null;
        }
    }, []);

    const debouncedSave = useMemo(() => {
        return debounce(async () => {
            if (isSavingRef.current) {
                hasPendingSaveRef.current = true;
                return;
            }

            try {
                if (!enabledRef.current || !guardRef.current?.()) return;

                isSavingRef.current = true;
                clearResetStateTimeout();
                setAutosaveState('running');
                setError(undefined);
                await handlerRef.current?.();

                setAutosaveState('success');
                resetStateTimeoutRef.current = setTimeout(() => setAutosaveState(null), 3000);
            } catch (err) {
                setError((err as Error).message);
                setAutosaveState('error');
            } finally {
                isSavingRef.current = false;

                if (hasPendingSaveRef.current && enabledRef.current) {
                    hasPendingSaveRef.current = false;
                    debouncedSave();
                }
            }
        }, 800);
    }, [clearResetStateTimeout]);

    useEffect(() => {
        return () => {
            debouncedSave.cancel();
            clearResetStateTimeout();
        };
    }, [clearResetStateTimeout, debouncedSave]);

    const autosave = useCallback(() => {
        debouncedSave();
    }, [debouncedSave]);

    useEffect(() => {
        if (!enabled) {
            debouncedSave.cancel();
            hasPendingSaveRef.current = false;
            if (error) setError(undefined);
            return;
        }

        autosave();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...trigger, enabled]);

    return { autosaveState: autosaveState, error };
};
